import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowLeft, Building2, CalendarCheck, CalendarPlus, CalendarX2, Clock, Languages, MapPin, Video } from 'lucide-react';
import { useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { useI18n, type Locale, type TranslationKey } from '../lib/i18n';
import { formatShortTime } from '../lib/format';
import { VigLogo } from '../components/Logo';
import { Button, Card, Field, Input, LoadingBlock, Textarea } from '../components/ui';

/**
 * A booking link's page (operator, 2026-11-05: "create a page with public link to
 * sent to anyone to choose meeting"). Open to anyone who has the link, signed in
 * or not: who it is with, the free times in the hours they take meetings — shown
 * in the visitor's own timezone — and, once a time is picked, their name and
 * email. Booked, it is in its owner's calendar at once; the visitor gets the
 * details by email and their own link to see or cancel it (pages/Booking.tsx).
 */

export const BOOKING_BASE = '/api/v1/meeting-booking/public';

type Place = 'OFFICE' | 'OUTSIDE' | 'ONLINE';

interface Person {
  fullName: string;
  fullNameAr: string | null;
  jobTitle: string | null;
}

interface BookingLinkPage {
  host: Person;
  company: { name: string; nameAr: string };
  title: string | null;
  defaultTitle: { en: string; ar: string };
  intro: string | null;
  durationMinutes: number;
  place: Place;
  timezone: string;
  days: Array<{ day: string; slots: string[] }>;
}

/** A guest's booking — what `pages/Booking.tsx` shows too. */
export interface GuestBooking {
  guest: { name: string; email: string; phone: string | null; company: string | null; note: string | null };
  host: Person;
  company: { name: string; nameAr: string };
  meeting: {
    startsAt: string;
    endsAt: string;
    place: Place;
    location: string | null;
    status: 'SCHEDULED' | 'STARTED' | 'IN_PROGRESS' | 'DONE' | 'CANCELLED';
    cancelledAt: string | null;
    cancelledBy: 'GUEST' | 'HOST' | null;
    cancelNote: string | null;
  };
  canCancel: boolean;
}

/** No session: plain requests, and our error envelope read the same way. */
export async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BOOKING_BASE}/${path}`, {
    ...init,
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
  });
  const payload = await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError(res.status, payload?.error ?? { code: 'NETWORK_ERROR', message: `Request failed (${res.status}).` });
  }
  return payload?.data as T;
}

export const PLACE_ICON: Record<Place, typeof Video> = { OFFICE: Building2, OUTSIDE: MapPin, ONLINE: Video };
const LOCALE_TAG: Record<Locale, string> = { en: 'en-GB', ar: 'ar-EG-u-nu-latn' };

/** The browser's own timezone, which every time here is shown in. */
export const visitorZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

const pad = (n: number) => String(n).padStart(2, '0');
/** "2026-11-08" — a day on the visitor's own calendar. */
const localDay = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** "Sun, 8 Nov" — a day, in the reader's language. */
export function dayLabel(value: string | Date, locale: Locale, year = false): string {
  return new Intl.DateTimeFormat(LOCALE_TAG[locale], {
    weekday: year ? 'long' : 'short',
    day: 'numeric',
    month: year ? 'long' : 'short',
    ...(year ? { year: 'numeric' } : {}),
  }).format(new Date(value));
}

/** "Sunday, 8 November 2026 · 10:00–10:30" — a booked meeting's time, on the visitor's clock. */
export function whenText(m: { startsAt: string; endsAt: string }, locale: Locale): string {
  return `${dayLabel(m.startsAt, locale, true)} · ${formatShortTime(m.startsAt, locale)}–${formatShortTime(m.endsAt, locale)}`;
}

/** "30 minutes", "1 hour 30 minutes" — and Arabic's own counting. */
export function lengthText(minutes: number, locale: Locale): string {
  const ar = locale === 'ar';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const hours = ar ? (h === 1 ? 'ساعة' : h === 2 ? 'ساعتان' : `${h} ساعات`) : h === 1 ? '1 hour' : `${h} hours`;
  const mins = ar ? (m > 2 && m <= 10 ? `${m} دقائق` : `${m} دقيقة`) : `${m} minutes`;
  if (h === 0) return mins;
  if (m === 0) return hours;
  return ar ? `${hours} و${mins}` : `${hours} ${mins}`;
}

/** The page's header: our logo, and the other language. */
export function PublicHeader() {
  const { locale, setLocale } = useI18n();
  return (
    <header className="border-b hairline bg-[var(--surface-raised)]">
      <div className="mx-auto flex h-16 max-w-5xl items-center justify-between gap-3 px-4">
        <VigLogo className="h-7 w-auto text-[var(--text-primary)]" title="Viganium" />
        <button
          type="button"
          onClick={() => setLocale(locale === 'en' ? 'ar' : 'en')}
          lang={locale === 'en' ? 'ar' : 'en'}
          className="flex h-9 items-center gap-1.5 rounded-sm px-2 text-body-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
        >
          <Languages className="h-4 w-4" aria-hidden />
          {locale === 'en' ? 'العربية' : 'English'}
        </button>
      </div>
    </header>
  );
}

/** A link that opens nothing, or one not taking bookings — said plainly. */
export function Unavailable({ reason }: { reason: 'NOT_FOUND' | 'OFF' | 'ERROR' }) {
  const { t } = useI18n();
  return (
    <Card className="mx-auto max-w-lg text-center">
      <CalendarX2 className="mx-auto mb-3 h-9 w-9 text-[var(--text-tertiary)]" aria-hidden />
      <h1 className="text-h2 text-[var(--text-primary)]">{t(`meet.unavailable.${reason}` as TranslationKey)}</h1>
      <p className="mt-2 text-body-sm text-muted">{t(reason === 'ERROR' ? 'share.unavailable.tryAgain' : 'meet.unavailable.hint')}</p>
    </Card>
  );
}

/** Why a link does not open, from what the API said. */
export function unavailableReason(error: unknown): 'NOT_FOUND' | 'OFF' | 'ERROR' {
  if (error instanceof ApiError && error.code === 'MEETING_BOOKING_UNAVAILABLE') {
    return (error.details as { reason?: string } | undefined)?.reason === 'OFF' ? 'OFF' : 'NOT_FOUND';
  }
  return 'ERROR';
}

/** One fact of the meeting: an icon, a label, its value. */
export function Fact({ icon: Icon, label, children }: { icon: typeof Clock; label: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
      <div className="min-w-0">
        <span className="block text-caption text-faint">{label}</span>
        <span className="block break-words text-body-sm text-[var(--text-primary)]">{children}</span>
      </div>
    </div>
  );
}

export function MeetPage() {
  const { token = '' } = useParams();
  const { t, locale } = useI18n();
  const queryClient = useQueryClient();
  const zone = useMemo(visitorZone, []);
  const [day, setDay] = useState<string | null>(null);
  const [slot, setSlot] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', note: '' });
  const [problem, setProblem] = useState<{ field: string | null; text: string } | null>(null);
  const [booked, setBooked] = useState<(GuestBooking & { token: string; emailed: boolean }) | null>(null);
  const timesRef = useRef<HTMLDivElement>(null);

  const page = useQuery({
    queryKey: ['meet', token],
    queryFn: () => call<BookingLinkPage>(`pages/${token}`),
    retry: false,
    // A time someone else books goes from the page within the minute.
    refetchInterval: booked ? false : 60_000,
  });

  // The free times on the visitor's own calendar: a time near midnight may fall on another day than the company's.
  const days = useMemo(() => {
    const byDay = new Map<string, string[]>();
    for (const d of page.data?.days ?? []) {
      for (const s of d.slots) {
        const key = localDay(new Date(s));
        byDay.set(key, [...(byDay.get(key) ?? []), s]);
      }
    }
    return [...byDay.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([key, slots]) => ({ key, slots: slots.sort() }));
  }, [page.data]);

  const chosenDay = days.find((d) => d.key === day) ?? days[0] ?? null;

  const book = useMutation({
    mutationFn: () =>
      call<GuestBooking & { token: string; emailed: boolean }>(`pages/${token}/book`, {
        method: 'POST',
        body: JSON.stringify({
          startsAt: slot,
          name: form.name,
          email: form.email,
          phone: form.phone || undefined,
          company: form.company || undefined,
          note: form.note || undefined,
          locale,
          timezone: zone,
        }),
      }),
    onSuccess: (result) => {
      setBooked(result);
      window.scrollTo({ top: 0 });
    },
    onError: (e: unknown) => {
      const details = (e instanceof ApiError ? e.details : undefined) as { reason?: string; field?: string; max?: number } | undefined;
      const reason = details?.reason ?? '';
      if (e instanceof ApiError && e.code === 'MEETING_BOOKING_REFUSED' && reason === 'TAKEN') {
        setSlot(null);
        void queryClient.invalidateQueries({ queryKey: ['meet', token] });
        setProblem({ field: null, text: t('meet.refusal.TAKEN') });
      } else if (e instanceof ApiError && e.code === 'MEETING_BOOKING_REFUSED' && reason === 'TOO_MANY') {
        setProblem({ field: null, text: t('meet.refusal.TOO_MANY', { n: details?.max ?? 3 }) });
      } else if (['NAME', 'EMAIL', 'PHONE'].includes(reason)) {
        setProblem({ field: details?.field ?? null, text: t(`meet.refusal.${reason}` as TranslationKey) });
      } else if (e instanceof ApiError && e.status === 429) {
        setProblem({ field: null, text: t('meet.refusal.busy') });
      } else if (e instanceof ApiError && e.code === 'MEETING_BOOKING_UNAVAILABLE') {
        void queryClient.invalidateQueries({ queryKey: ['meet', token] });
      } else {
        setProblem({ field: null, text: t('meet.refusal.error') });
      }
    },
  });

  const data = page.data;
  const title = data ? data.title || data.defaultTitle[locale] : '';
  const host = data ? (locale === 'ar' && data.host.fullNameAr) || data.host.fullName : '';
  const company = data ? (locale === 'ar' ? data.company.nameAr : data.company.name) : '';

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setProblem(null);
    if (!slot || book.isPending) return;
    book.mutate();
  };

  return (
    <div className="min-h-full bg-[var(--surface-page)]">
      <PublicHeader />
      <main className="mx-auto max-w-5xl px-4 py-8">
        {page.isLoading ? (
          <LoadingBlock />
        ) : !data ? (
          <Unavailable reason={unavailableReason(page.error)} />
        ) : booked ? (
          <Booked booking={booked} title={title} />
        ) : (
          <>
            <div className="mb-6">
              <p className="text-mono-sm uppercase tracking-widest text-[var(--text-tertiary)]">{t('meet.kicker')}</p>
              <h1 className="mt-1 break-words text-h1 text-[var(--text-primary)]" dir="auto">
                {title}
              </h1>
              <p className="mt-2 text-body text-muted">
                {t('meet.with', { name: host })}
                {data.host.jobTitle && <> · {data.host.jobTitle}</>} · {company}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Chip icon={Clock}>{lengthText(data.durationMinutes, locale)}</Chip>
                <Chip icon={PLACE_ICON[data.place]}>{t(`meet.place.${data.place}` as TranslationKey)}</Chip>
              </div>
              {data.intro && (
                <p className="mt-4 max-w-3xl whitespace-pre-wrap text-body text-[var(--text-secondary)]" dir="auto">
                  {data.intro}
                </p>
              )}
            </div>

            {days.length === 0 ? (
              <Card className="text-center">
                <CalendarX2 className="mx-auto mb-3 h-9 w-9 text-[var(--text-tertiary)]" aria-hidden />
                <h2 className="text-h3 text-[var(--text-primary)]">{t('meet.noTimes')}</h2>
                <p className="mt-1 text-body-sm text-muted">{t('meet.noTimesHint')}</p>
              </Card>
            ) : !slot ? (
              <div className="grid gap-5 md:grid-cols-5">
                <Card className="md:col-span-3">
                  <h2 className="mb-3 text-h3 text-[var(--text-primary)]">{t('meet.pickDay')}</h2>
                  <div className="grid max-h-[26rem] grid-cols-2 gap-2 overflow-y-auto pe-1 sm:grid-cols-3">
                    {days.map((d) => {
                      const on = chosenDay?.key === d.key;
                      return (
                        <button
                          key={d.key}
                          type="button"
                          aria-pressed={on}
                          onClick={() => {
                            setDay(d.key);
                            // On a phone the times are under the days: bring them into view.
                            if (window.innerWidth < 768) {
                              requestAnimationFrame(() => timesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
                            }
                          }}
                          className={clsx(
                            'rounded-md border px-3 py-2 text-start transition-colors duration-fast',
                            on
                              ? 'border-primary bg-primary/10 text-[var(--text-primary)] ring-1 ring-primary'
                              : 'hairline bg-[var(--surface-raised)] text-[var(--text-primary)] hover:border-primary/60',
                          )}
                        >
                          <span className="block text-body-sm font-medium">{dayLabel(`${d.key}T12:00:00`, locale)}</span>
                          <span className="block text-caption text-muted">{t('meet.freeTimes', { n: d.slots.length })}</span>
                        </button>
                      );
                    })}
                  </div>
                </Card>
                <Card className="md:col-span-2">
                  <div ref={timesRef} className="scroll-mt-8" />
                  <h2 className="mb-1 text-h3 text-[var(--text-primary)]">{t('meet.pickTime')}</h2>
                  <p className="mb-3 text-caption text-faint">{t('meet.yourZone', { zone })}</p>
                  {chosenDay ? (
                    <>
                      <p className="mb-2 text-body-sm font-medium text-[var(--text-secondary)]">
                        {dayLabel(`${chosenDay.key}T12:00:00`, locale, true)}
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        {chosenDay.slots.map((s) => (
                          <button
                            key={s}
                            type="button"
                            onClick={() => {
                              setSlot(s);
                              setProblem(null);
                            }}
                            className="h-10 rounded-md border hairline bg-[var(--surface-raised)] text-body-sm font-medium tabular text-[var(--text-primary)] transition-colors duration-fast hover:border-primary hover:text-primary-600 dark:hover:text-primary-400"
                          >
                            {formatShortTime(s, locale)}
                          </button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-body-sm text-muted">{t('meet.pickDayFirst')}</p>
                  )}
                  {problem && !problem.field && <p className="mt-3 text-body-sm text-error-fg dark:text-error-dark">{problem.text}</p>}
                </Card>
              </div>
            ) : (
              <Card className="mx-auto max-w-2xl">
                <button
                  type="button"
                  onClick={() => setSlot(null)}
                  className="mb-4 inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]"
                >
                  <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
                  {t('meet.change')}
                </button>
                <div className="mb-5 rounded-md bg-[var(--surface-sunken)] px-4 py-3">
                  <p className="text-body font-medium text-[var(--text-primary)] tabular">
                    {whenText(
                      { startsAt: slot, endsAt: new Date(new Date(slot).getTime() + data.durationMinutes * 60_000).toISOString() },
                      locale,
                    )}
                  </p>
                  <p className="text-caption text-faint">
                    {lengthText(data.durationMinutes, locale)} · {t('meet.with', { name: host })} · {zone}
                  </p>
                </div>
                <h2 className="mb-4 text-h3 text-[var(--text-primary)]">{t('meet.yourDetails')}</h2>
                <form className="grid gap-4 sm:grid-cols-2" onSubmit={submit}>
                  <Field label={t('meet.name')} required error={problem?.field === 'name' ? problem.text : null}>
                    <Input
                      value={form.name}
                      required
                      minLength={2}
                      maxLength={100}
                      autoComplete="name"
                      dir="auto"
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                    />
                  </Field>
                  <Field
                    label={t('meet.email')}
                    required
                    hint={t('meet.emailHint')}
                    error={problem?.field === 'email' ? problem.text : null}
                  >
                    <Input
                      type="email"
                      value={form.email}
                      required
                      maxLength={254}
                      autoComplete="email"
                      dir="ltr"
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />
                  </Field>
                  <Field label={t('meet.phone')} error={problem?.field === 'phone' ? problem.text : null}>
                    <Input
                      type="tel"
                      value={form.phone}
                      maxLength={40}
                      autoComplete="tel"
                      dir="ltr"
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    />
                  </Field>
                  <Field label={t('meet.company')}>
                    <Input
                      value={form.company}
                      maxLength={100}
                      autoComplete="organization"
                      dir="auto"
                      onChange={(e) => setForm({ ...form, company: e.target.value })}
                    />
                  </Field>
                  <Field label={t('meet.note')} className="sm:col-span-2">
                    <Textarea value={form.note} maxLength={1000} dir="auto" onChange={(e) => setForm({ ...form, note: e.target.value })} />
                  </Field>
                  {problem && !problem.field && (
                    <p className="text-body-sm text-error-fg dark:text-error-dark sm:col-span-2">{problem.text}</p>
                  )}
                  <div className="flex justify-end sm:col-span-2">
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      loading={book.isPending}
                      icon={<CalendarCheck className="h-4 w-4" aria-hidden />}
                    >
                      {t('meet.book')}
                    </Button>
                  </div>
                </form>
              </Card>
            )}

            <p className="mt-8 text-center text-caption text-faint">{t('meet.footer', { company })}</p>
          </>
        )}
      </main>
    </div>
  );
}

function Chip({ icon: Icon, children }: { icon: typeof Clock; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border hairline bg-[var(--surface-raised)] px-3 py-1 text-body-sm text-[var(--text-secondary)]">
      <Icon className="h-3.5 w-3.5 text-[var(--text-tertiary)]" aria-hidden />
      {children}
    </span>
  );
}

/** Booked: what, when and where — the link or the address now — and the guest's own link. */
function Booked({ booking, title }: { booking: GuestBooking & { token: string; emailed: boolean }; title: string }) {
  const { t, locale } = useI18n();
  const host = (locale === 'ar' && booking.host.fullNameAr) || booking.host.fullName;
  const company = locale === 'ar' ? booking.company.nameAr : booking.company.name;
  const minutes = Math.round((new Date(booking.meeting.endsAt).getTime() - new Date(booking.meeting.startsAt).getTime()) / 60_000);
  return (
    <Card className="mx-auto max-w-2xl">
      <div className="mb-5 flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-success-bg text-success-fg dark:bg-success/12 dark:text-success">
          <CalendarCheck className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0">
          <h1 className="text-h2 text-[var(--text-primary)]">{t('meet.booked')}</h1>
          <p className="mt-1 text-body-sm text-muted">
            {booking.emailed ? t('meet.bookedEmailed', { email: booking.guest.email }) : t('meet.bookedNoEmail')}
          </p>
        </div>
      </div>
      <MeetingFacts booking={booking} />
      <div className="mt-6 flex flex-wrap gap-2">
        <Button
          icon={<CalendarPlus className="h-4 w-4" aria-hidden />}
          onClick={() =>
            saveIcs(booking, {
              title: `${title} — ${company}`,
              description: `${t('meet.withLabel')}: ${host} — ${company}`,
              where: booking.meeting.location ?? t(`meet.place.${booking.meeting.place}` as TranslationKey),
              uid: booking.token,
            })
          }
        >
          {t('meet.addToCalendar')}
        </Button>
        <Link to={`/booking/${booking.token}`}>
          <Button variant="primary">{t('meet.seeBooking')}</Button>
        </Link>
      </div>
      <p className="mt-4 text-caption text-faint">{t('meet.yourZone', { zone: visitorZone() })}</p>
    </Card>
  );
}

/** When, how long, where — with the link for an online meeting — and with whom. */
export function MeetingFacts({ booking }: { booking: GuestBooking }) {
  const { t, locale } = useI18n();
  const { meeting } = booking;
  const host = (locale === 'ar' && booking.host.fullNameAr) || booking.host.fullName;
  const company = locale === 'ar' ? booking.company.nameAr : booking.company.name;
  const minutes = Math.round((new Date(meeting.endsAt).getTime() - new Date(meeting.startsAt).getTime()) / 60_000);
  const link = meeting.place === 'ONLINE' && meeting.location && /^https?:\/\//i.test(meeting.location) ? meeting.location : null;
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Fact icon={CalendarCheck} label={t('meet.when')}>
        <span className="tabular">{whenText(meeting, locale)}</span>
      </Fact>
      <Fact icon={Clock} label={t('meet.length')}>
        {lengthText(minutes, locale)}
      </Fact>
      <Fact icon={PLACE_ICON[meeting.place]} label={t('meet.where')}>
        {link ? (
          <a href={link} target="_blank" rel="noopener noreferrer" dir="ltr" className="break-all text-primary-600 hover:underline dark:text-primary-400">
            {link}
          </a>
        ) : (
          <>
            {t(`booking.place.${meeting.place}` as TranslationKey)}
            {meeting.location && (
              <span className="block whitespace-pre-wrap text-muted" dir="auto">
                {meeting.location}
              </span>
            )}
          </>
        )}
      </Fact>
      <Fact icon={Building2} label={t('meet.withLabel')}>
        {host}
        {booking.host.jobTitle && <span className="text-muted"> · {booking.host.jobTitle}</span>}
        <span className="block text-muted">{company}</span>
      </Fact>
    </div>
  );
}

/** The booked meeting as a calendar file — any calendar app adds it. */
function saveIcs(booking: GuestBooking, words: { title: string; description: string; where: string; uid: string }) {
  const stamp = (iso: string) => new Date(iso).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const escape = (text: string) => text.replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/([,;])/g, '\\$1');
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Viganium//Booking link//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${words.uid}@viganium`,
    `DTSTAMP:${stamp(new Date().toISOString())}`,
    `DTSTART:${stamp(booking.meeting.startsAt)}`,
    `DTEND:${stamp(booking.meeting.endsAt)}`,
    `SUMMARY:${escape(words.title)}`,
    `DESCRIPTION:${escape(words.description)}`,
    `LOCATION:${escape(words.where)}`,
    'END:VEVENT',
    'END:VCALENDAR',
    '',
  ].join('\r\n');
  const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'meeting.ics';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
